/**
 * OutputStructure tree helpers.
 *
 * `OutputStructure.levels` is a tree: each level carries a
 * `children: OutputLevel[]` field. These helpers walk and mutate the
 * tree with path-based addressing so UI components don't have to know
 * whether a given level lives at depth 0 or deeper.
 */

export interface OutputLevel {
  element_name: string;
  key_column: string;
  identity_columns?: string[];
  parent_key?: string | null;
  source_table?: string | null;
  fields?: string[];
  attributes?: Record<string, string>;
  ref_id?: string | null;
  scope?: 'data' | 'metadata' | 'both';
  children?: OutputLevel[];
}

export interface OutputStructure {
  format: string;
  root_element?: string | null;
  root_attributes?: Record<string, string>;
  levels: OutputLevel[];
  namespaces?: Record<string, string>;
  preserved_sections?: Array<{ content: string; position: string; label?: string | null }>;
  preserved_sections_context?: Record<string, string>;
  template_ref?: string | null;
  multi_file?: boolean;
  file_split_path?: string[] | null;
}

/**
 * DFS pre-order list of every level in the tree.
 *
 * Returns an array (rather than a generator) so the helper is safe to
 * consume under the project's ES5 compile target, where `for..of` over
 * user-defined iterables would require ``--downlevelIteration``.
 */
export function iterAllLevels(
  structureOrLevels: OutputStructure | OutputLevel[] | null | undefined,
): OutputLevel[] {
  if (!structureOrLevels) return [];
  const top = Array.isArray(structureOrLevels)
    ? structureOrLevels
    : structureOrLevels.levels || [];
  const out: OutputLevel[] = [];
  const walk = (level: OutputLevel): void => {
    out.push(level);
    for (const child of level.children || []) walk(child);
  };
  for (const lvl of top) walk(lvl);
  return out;
}

/**
 * Returns true when the structure contains at least one level anywhere
 * in its tree.
 */
export function hasAnyLevel(structure: OutputStructure | null | undefined): boolean {
  if (!structure) return false;
  return iterAllLevels(structure).length > 0;
}

/**
 * Flat-count of every level across the entire tree.
 */
export function totalLevelCount(structure: OutputStructure | null | undefined): number {
  return iterAllLevels(structure).length;
}

/**
 * Resolve a slash-joined path ("Parent/Child/Grandchild") to a specific
 * level node, or null when the path doesn't resolve. Useful as a
 * stable handle for UI edit state (expanded/collapsed rows, focused
 * inputs, etc.) that survives sibling inserts/removes.
 */
export function findLevelByPath(
  structure: OutputStructure,
  path: string[],
): OutputLevel | null {
  if (path.length === 0) return null;
  let siblings = structure.levels;
  let found: OutputLevel | null = null;
  for (const name of path) {
    found = siblings.find((l) => l.element_name === name) || null;
    if (!found) return null;
    siblings = found.children || [];
  }
  return found;
}

/**
 * Return a NEW structure with the level at `path` replaced by
 * `updater(level)`. Non-mutating — components passing the result to
 * setState trigger a normal React re-render.
 */
export function updateLevelAtPath(
  structure: OutputStructure,
  path: string[],
  updater: (level: OutputLevel) => OutputLevel,
): OutputStructure {
  if (path.length === 0) return structure;
  const nextLevels = updateSiblingsAtPath(structure.levels, path, updater);
  return { ...structure, levels: nextLevels };
}

function updateSiblingsAtPath(
  siblings: OutputLevel[],
  path: string[],
  updater: (level: OutputLevel) => OutputLevel,
): OutputLevel[] {
  const [head, ...rest] = path;
  return siblings.map((lvl) => {
    if (lvl.element_name !== head) return lvl;
    if (rest.length === 0) return updater(lvl);
    return {
      ...lvl,
      children: updateSiblingsAtPath(lvl.children || [], rest, updater),
    };
  });
}

/**
 * Cross-reference integrity check: returns the list of ``ref_id``
 * values declared on levels that do NOT resolve to a key in
 * ``preserved_sections_context`` and are not also defined as the
 * ``ref_id`` of another level in the same tree. Used by pre-promotion
 * validation (#444).
 *
 * A ref_id is considered resolved when it appears as a key in
 * ``preserved_sections_context`` (the rendered context dict the
 * template parser populates from preserved sections). Until the
 * parser fills that dict for hand-authored or partially-interpreted
 * drafts, fall back to checking whether at least one preserved
 * section's ``label`` matches the ref_id — labels are operator-set
 * identifiers for content blocks.
 */
export function findUnresolvedRefIds(
  structure: OutputStructure | null | undefined,
): Array<{ level: string; ref_id: string }> {
  if (!structure) return [];
  const ctxKeys = new Set(Object.keys(structure.preserved_sections_context || {}));
  const sectionLabels = new Set(
    (structure.preserved_sections || [])
      .map((s) => s.label)
      .filter((l): l is string => Boolean(l)),
  );
  const out: Array<{ level: string; ref_id: string }> = [];
  for (const lvl of iterAllLevels(structure)) {
    const r = lvl.ref_id;
    if (!r) continue;
    if (ctxKeys.has(r) || sectionLabels.has(r)) continue;
    out.push({ level: lvl.element_name, ref_id: r });
  }
  return out;
}

/**
 * Human-readable hierarchy path for a column — returns e.g.
 * "ClinicalData ▸ SubjectData" when `col` is the key_column of
 * SubjectData under ClinicalData, or `null` when the column does not
 * appear anywhere in the structure.
 */
export function findColumnPathInStructure(
  structure: OutputStructure | null | undefined,
  columnName: string,
): { path: string[]; role: 'key' | 'field'; level: OutputLevel } | null {
  if (!structure) return null;
  const stack: Array<{ node: OutputLevel; path: string[] }> =
    (structure.levels || []).map((n) => ({ node: n, path: [n.element_name] }));
  while (stack.length) {
    const { node, path } = stack.pop()!;
    if (node.key_column === columnName) {
      return { path, role: 'key', level: node };
    }
    if ((node.fields || []).includes(columnName)) {
      return { path, role: 'field', level: node };
    }
    for (const child of node.children || []) {
      stack.push({ node: child, path: [...path, child.element_name] });
    }
  }
  return null;
}
